// eslint-disable-next-line eag/match-named-export
import {isNotNil, uniq} from 'ramda';

import {getActiveCustomSections} from '../components/Section/utils/getActiveCustomSections';
import {getActiveSections} from '../components/Section/utils/getActiveSections';
import {getHiddenSections} from '../utils/getHiddenSections';
import {useGetDataForDocumentation} from './useGetDataForDocumentation';
import {useGetDataForFeatures} from './useGetDataForFeatures';
import {useGetDataForMechanics} from './useGetDataForMechanics';
import {useGetDataForPhotoDocumentation} from './useGetDataForPhotoDocumentation';
import {useGetDataForTestDrive} from './useGetDataForTestDrive';
import {useGetDataForVideoWalkthrough} from './useGetDataForVideoWalkthrough';
import {useGetDataForWheelsAndTires} from './useGetDataForWheelsAndTires';
import {useGetDataServiceHistory} from './useGetDataServiceHistory';
import {useGetDigitalCertificateData} from './useGetDigitalCertificateData';
import {useLocale} from './useLocale';

export const useGetSectionsData = () => {
  const {vehicleAudit} = useGetDigitalCertificateData();
  const locale = useLocale();

  const showEmptyFields = !!vehicleAudit?.showEmptyFields;

  const wheelsAndTiresData = useGetDataForWheelsAndTires(vehicleAudit, locale);
  const documentationData = useGetDataForDocumentation(vehicleAudit, locale);
  const testDriveData = useGetDataForTestDrive(vehicleAudit);
  const serviceHistoryData = useGetDataServiceHistory(vehicleAudit, locale, showEmptyFields);
  const mechanicsData = useGetDataForMechanics(vehicleAudit, locale);
  const featuresData = useGetDataForFeatures(vehicleAudit, locale);
  const photoDocumentationData = useGetDataForPhotoDocumentation(vehicleAudit);
  const videoWalkthroughData = useGetDataForVideoWalkthrough(vehicleAudit);

  const hiddenSections = getHiddenSections(
    [
      wheelsAndTiresData.sectionConfig,
      documentationData.sectionConfig,
      testDriveData.sectionConfig,
      serviceHistoryData.sectionConfig,
      mechanicsData.sectionConfig,
    ],
    showEmptyFields
  );

  const shouldBeHiddenOnAllModes = [
    ...(photoDocumentationData.sectionConfig.isHidden
      ? [photoDocumentationData.sectionConfig.name]
      : []),
    ...(featuresData.sectionConfig.isHidden ? [featuresData.sectionConfig.name] : []),
    ...(videoWalkthroughData.sectionConfig.isHidden
      ? [videoWalkthroughData.sectionConfig.name]
      : []),
  ];

  // An attached equipment PDF activates the section even without an Equipment category.
  const sections = uniq([
    ...getActiveSections(vehicleAudit?.auditStructure?.categories),
    ...getActiveCustomSections(),
    ...(isNotNil(featuresData.equipmentDocument) ? [featuresData.sectionConfig.name] : []),
  ]);

  return {
    wheelsAndTiresData,
    documentationData,
    testDriveData,
    serviceHistoryData,
    mechanicsData,
    featuresData,
    hiddenSections: [...hiddenSections, ...shouldBeHiddenOnAllModes],
    photoDocumentationData,
    videoWalkthroughData,
    sections,
    similarCarsData: vehicleAudit?.vehicle,
    showEmptyFields,
  };
};
